import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import createSafeContext from '@paradigm/utils/createSafeContext';
import { getUserId } from '@paradigm/store/src/auth/selectors';
import { logException } from '@paradigm/logging/src/logging';
import { usePreferencesStore } from '@paradigm/features/src/preferences/store';

import {
  clearAgentSession,
  getAgentUrl,
  setAgentUrl,
  getAgentToken,
  setAgentToken,
} from '#/api/terminal/agent-url';
import {
  clearChatHistory,
  loadChatHistory,
  saveChatHistory,
} from '#/api/terminal/chat-history-storage';
import {
  AgentWebSocket,
  createAgentWebSocket,
} from '#/api/terminal/createAgentWebSocket';
import {
  AgentConnectionStatus,
  ApprovalRequest,
  ChatMessage,
  GatewayFrame,
} from '#/api/terminal/types';

interface AgentState {
  readonly status: AgentConnectionStatus;
  readonly errorMessage: string | null;
  readonly messages: readonly ChatMessage[];
  readonly agentUrl: string | null;
  /**
   * Ephemeral "the agent is doing something" indicator, driven off the
   * OpenClaw `agent` event's `data.state` field (and set optimistically on
   * `sendMessage`). Cleared the moment any concrete assistant output
   * arrives — delta, approval, error, or `*.done`.
   */
  readonly processing: {
    readonly label: string;
    readonly detail?: string;
  } | null;
}

interface AgentActions {
  readonly sendMessage: (content: string) => void;
  readonly connect: (url?: string) => void;
  readonly disconnect: () => void;
  readonly clearMessages: () => void;
  readonly respondToApproval: (approvalId: string, approved: boolean) => void;
  readonly request: <T = unknown>(
    method: string,
    params?: Record<string, unknown>,
  ) => Promise<T>;
  readonly requestPairing: () => Promise<string>;
  readonly setUrl: (url: string) => void;
}

const INITIAL_STATE: AgentState = {
  status: 'disconnected',
  errorMessage: null,
  messages: [],
  agentUrl: null,
  processing: null,
};

const PROCESSING_STATE_LABELS: Readonly<Record<string, string>> = {
  thinking: 'Thinking',
  reasoning: 'Thinking',
  planning: 'Planning',
  running: 'Running',
  tool_use: 'Calling tool',
  tool_call: 'Calling tool',
  tool: 'Calling tool',
  searching: 'Searching',
  analyzing: 'Analyzing',
  streaming: 'Writing',
  writing: 'Writing',
};

const TERMINAL_PROCESSING_STATES: ReadonlySet<string> = new Set([
  'done',
  'complete',
  'completed',
  'finished',
  'idle',
  'ready',
]);

function humanizeState(state: string): string {
  return state
    .replace(/[_-]+/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim()
    .replace(/\b\w/gu, (c) => c.toUpperCase());
}

function resolveProcessingLabel(state: string): string {
  const key = state.toLowerCase();
  return PROCESSING_STATE_LABELS[key] ?? humanizeState(state);
}

function closeStreamingMessages(
  messages: readonly ChatMessage[],
): readonly ChatMessage[] {
  if (!messages.some((m) => m.isStreaming === true)) return messages;
  return messages.map((m) =>
    m.isStreaming === true ? { ...m, isStreaming: false } : m,
  );
}

export const [useAgentState, AgentStateProvider] =
  createSafeContext<AgentState>('AgentState');

export const [useAgentActions, AgentActionsProvider] =
  createSafeContext<AgentActions>('AgentActions');

interface Props {
  readonly children: ReactNode;
  /**
   * Static framing prepended to every outbound `chat.send` payload but NOT
   * shown in the chat bubble. Use it to tell the agent what context it's
   * operating in (e.g. "You are analyzing paradigm options block trades")
   * so a generic OpenClaw agent doesn't fall back to its default training.
   */
  readonly systemPreamble?: string;
}

let nextMsgId = Date.now();

export function AgentProvider({ children, systemPreamble }: Props) {
  const userId = useSelector(getUserId);
  // The auth reducer resets `user.id` to `0` on `logoutSuccess` instead
  // of `null`, so treat falsy / "empty user" as logged-out. Otherwise
  // we'd hold the agent session open for a phantom user `0` and never
  // run the cleanup branch below.
  const userIdStr =
    userId != null && userId !== 0 && String(userId).length > 0
      ? String(userId)
      : null;
  const userIdRef = useRef<string | null>(userIdStr);
  userIdRef.current = userIdStr;

  // Server-synced URL and token are the source of truth across devices;
  // localStorage is a fast read for the WebSocket layer below it.
  const persistedUrl = usePreferencesStore((s) => s.aiAgentUrl);
  const setPersistedUrl = usePreferencesStore((s) => s.setAiAgentUrl);
  const setPersistedUrlRef = useRef(setPersistedUrl);
  setPersistedUrlRef.current = setPersistedUrl;

  const persistedToken = usePreferencesStore((s) => s.aiAgentToken);
  const setPersistedToken = usePreferencesStore((s) => s.setAiAgentToken);
  const setPersistedTokenRef = useRef(setPersistedToken);
  setPersistedTokenRef.current = setPersistedToken;
  // Mirror into a ref so `connect` (memoized with empty deps to keep it
  // a stable callback) can read the latest value without listing
  // `persistedToken` as a dependency — adding it would force `connect`
  // to re-create on every token sync and re-fire the auth-hydration
  // effect, reconnecting the WS every 5s.
  const persistedTokenRef = useRef(persistedToken);
  persistedTokenRef.current = persistedToken;

  const systemPreambleRef = useRef(systemPreamble);
  systemPreambleRef.current = systemPreamble;

  const [state, setState] = useState<AgentState>(INITIAL_STATE);
  const wsRef = useRef<AgentWebSocket | null>(null);
  // Tracks the user we've already triggered a post-auth history fetch for
  // on the current WS instance, so we don't refetch on every `authenticated`
  // re-emit (e.g. after a heartbeat reconnect that lands us back here).
  const historyFetchedForRef = useRef<string | null>(null);

  const connect = useCallback((url?: string) => {
    const id = userIdRef.current;
    if (id == null) return;
    const agentUrl = url ?? getAgentUrl(id);
    if (agentUrl == null || agentUrl.length === 0) return;

    if (wsRef.current != null) {
      wsRef.current.close();
    }

    setState((prev) => ({
      ...prev,
      agentUrl,
      status: 'connecting',
    }));

    const ws = createAgentWebSocket(
      agentUrl,
      id,
      persistedTokenRef.current ?? undefined,
    );
    wsRef.current = ws;

    ws.onStatusChange((newStatus, errorMsg) => {
      setState((prev) => ({
        ...prev,
        status: newStatus,
        errorMessage:
          errorMsg ?? (newStatus === 'error' ? prev.errorMessage : null),
      }));

      // Reset the history-fetch guard whenever we leave 'authenticated', so
      // the next successful auth re-fetches.
      if (newStatus !== 'authenticated') {
        historyFetchedForRef.current = null;
      }

      if (
        newStatus === 'authenticated' &&
        historyFetchedForRef.current !== id
      ) {
        historyFetchedForRef.current = id;
        // Fetch recent chat history so reconnects show prior messages.
        // OpenClaw's `chat.history` is a tail-reader — it accepts `limit`
        // (hard max 1000) and ignores `before:`/`since:` cursor params, so
        // we always ask for the gateway's full window in one shot and
        // dedupe against locally-persisted messages on merge.
        fetchChatHistory(ws, setState);
      }
    });

    ws.onMessage((frame: GatewayFrame) => {
      handleFrame(frame, setState);
    });
  }, []);

  const disconnect = useCallback(() => {
    if (wsRef.current != null) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setState(() => ({
      status: 'disconnected',
      errorMessage: null,
      messages: [],
      agentUrl: null,
      processing: null,
    }));
  }, []);

  const sendMessage = useCallback((content: string) => {
    if (wsRef.current == null) return;

    const userMsg: ChatMessage = {
      id: `user-${nextMsgId++}`,
      role: 'user',
      content,
      timestamp: Date.now(),
    };

    setState((prev) => ({
      ...prev,
      processing: { label: 'Thinking' },
      messages: [...prev.messages, userMsg],
    }));

    // Prepend the system preamble (if any) to the gateway payload but
    // keep the chat bubble showing only what the user typed.
    const preamble = systemPreambleRef.current;
    const payload =
      preamble != null && preamble.length > 0
        ? `${preamble}\n\n${content}`
        : content;
    wsRef.current.send(payload);
  }, []);

  const clearMessages = useCallback(() => {
    setState((prev) => ({
      ...prev,
      messages: [],
      processing: null,
    }));
    const id = userIdRef.current;
    if (id != null) clearChatHistory(id);
  }, []);

  const respondToApproval = useCallback(
    (approvalId: string, approved: boolean) => {
      setState((prev) => ({
        ...prev,
        messages: prev.messages.map((msg) =>
          msg.approval?.approvalId === approvalId
            ? {
                ...msg,
                approval: {
                  ...msg.approval,
                  state: approved ? ('approved' as const) : ('denied' as const),
                },
              }
            : msg,
        ),
      }));
      wsRef.current
        ?.request('approval.respond', { approvalId, approved })
        .catch((err: unknown) => logException('terminal:approval', err));
    },
    [],
  );

  const request = useCallback(
    async <T,>(
      method: string,
      params?: Record<string, unknown>,
    ): Promise<T> => {
      if (wsRef.current == null) {
        throw new Error('Not connected');
      }
      return wsRef.current.request<T>(method, params);
    },
    [],
  );

  const requestPairing = useCallback(async () => {
    if (wsRef.current == null) {
      throw new Error('Not connected');
    }
    return wsRef.current.requestPairing();
  }, []);

  const setUrl = useCallback((url: string) => {
    const id = userIdRef.current;
    if (id == null) return;
    // localStorage cache is keyed by user — keeps reads in `createAgentWebSocket`
    // / `agent-url.ts` fast and avoids a redux roundtrip on every reconnect.
    // The preferences slice is the durable, cross-device source of truth.
    setAgentUrl(id, url);
    setPersistedUrlRef.current(url);
    setState((prev) => ({ ...prev, agentUrl: url }));
  }, []);

  const actions = useMemo(
    () => ({
      sendMessage,
      connect,
      disconnect,
      clearMessages,
      respondToApproval,
      request,
      requestPairing,
      setUrl,
    }),
    [
      sendMessage,
      connect,
      disconnect,
      clearMessages,
      respondToApproval,
      request,
      requestPairing,
      setUrl,
    ],
  );

  // Auto-connect when a user id and stored URL are both available, and
  // tear down on sign-out / user switch.
  const hydratedUserRef = useRef<string | null>(null);
  const prevUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (userIdStr == null) {
      if (wsRef.current != null) {
        wsRef.current.close();
        wsRef.current = null;
      }
      // Tokens and chat history are per-user secrets; wipe them on
      // sign-out so a second user on the same browser doesn't inherit
      // them. The DB-synced preferences blob is the cross-device source
      // of truth and will re-seed both on the next login.
      const prev = prevUserIdRef.current;
      if (prev != null) {
        clearAgentSession(prev);
        clearChatHistory(prev);
      }
      prevUserIdRef.current = null;
      hydratedUserRef.current = null;
      setState({ ...INITIAL_STATE });
      return;
    }

    prevUserIdRef.current = userIdStr;

    if (hydratedUserRef.current === userIdStr) return;

    // DB preferences are authoritative across devices; localStorage is
    // only a per-device cache. If the two disagree, the DB wins — a stale
    // (or invalid testing-artifact) cached URL otherwise keeps the WS
    // pinned to an unreachable host and contributes to the connection
    // storm on side-panel reconnects.
    const localUrl = getAgentUrl(userIdStr);
    const hasPersisted = persistedUrl != null && persistedUrl.length > 0;
    const effectiveUrl =
      hasPersisted && persistedUrl != null && persistedUrl.length > 0
        ? persistedUrl
        : localUrl;
    if (effectiveUrl == null || effectiveUrl.length === 0) {
      if (wsRef.current != null) {
        wsRef.current.close();
        wsRef.current = null;
      }
      setState({ ...INITIAL_STATE });
      return;
    }

    if (wsRef.current != null) {
      wsRef.current.close();
      wsRef.current = null;
    }
    hydratedUserRef.current = userIdStr;

    // Sync localStorage to the DB value when they diverge. Stale tokens
    // are bound to the previous URL's gateway and would fail with
    // "gateway token mismatch", so clear them on a URL change.
    if (hasPersisted && persistedUrl !== localUrl) {
      setAgentUrl(userIdStr, effectiveUrl);
      if (localUrl != null && localUrl.length > 0) {
        clearAgentSession(userIdStr);
      }
    } else if (localUrl == null || localUrl.length === 0) {
      // Mirror the DB-only URL into localStorage so the WS / token
      // lookups (keyed off userId-scoped localStorage entries) work
      // without redux on subsequent reconnects.
      setAgentUrl(userIdStr, effectiveUrl);
    }

    // Similarly, sync the token from preferences to localStorage if available.
    // This allows pre-populated tokens (e.g., injected via SQL) to be used on
    // reconnects without hitting the preferences API.
    if (persistedToken != null && persistedToken.length > 0) {
      const localToken = getAgentToken(userIdStr);
      if (localToken == null || localToken.length === 0) {
        setAgentToken(userIdStr, persistedToken);
      }
    }

    // Hydrate the chat from localStorage so a refresh repaints recent
    // messages instantly — Telegram-style. The reconnect that follows will
    // request anything newer via `chat.history`.
    const persisted = loadChatHistory(userIdStr);
    setState({
      ...INITIAL_STATE,
      agentUrl: effectiveUrl,
      messages: persisted,
    });

    // Connect even without a token — the gateway lets us open a pairing
    // role and call `node.pair.request` for self-service pairing.
    connect(effectiveUrl);
  }, [userIdStr, persistedUrl, persistedToken, connect]);

  // Persist message changes to localStorage so a reload has something to
  // show. Gated on `hydratedUserRef` matching the current user — the first
  // render after hydration would otherwise rewrite the same blob, and a
  // stale render under a freshly-switched user would clobber the new
  // user's transcript.
  useEffect(() => {
    if (userIdStr == null) return;
    if (hydratedUserRef.current !== userIdStr) return;
    saveChatHistory(userIdStr, state.messages);
  }, [userIdStr, state.messages]);

  // Sync newly acquired tokens to preferences so they persist cross-device.
  // Check on a timer since tokens are set by the WebSocket handler and we
  // don't have direct visibility into when that happens.
  useEffect(() => {
    if (userIdStr == null) return;

    const checkAndSyncToken = () => {
      const localToken = getAgentToken(userIdStr);
      if (
        localToken != null &&
        localToken.length > 0 &&
        persistedToken !== localToken
      ) {
        setPersistedTokenRef.current(localToken);
      }
    };

    const interval = setInterval(checkAndSyncToken, 5000);
    return () => clearInterval(interval);
  }, [userIdStr, persistedToken]);

  useEffect(() => {
    return () => {
      wsRef.current?.close();
    };
  }, []);

  return (
    <AgentStateProvider value={state}>
      <AgentActionsProvider value={actions}>{children}</AgentActionsProvider>
    </AgentStateProvider>
  );
}

export function handleFrame(
  frame: GatewayFrame,
  setState: Dispatch<SetStateAction<AgentState>>,
) {
  if (frame.type === 'event') {
    const evt = frame;

    if (evt.event === 'agent') {
      const payload = evt.payload as
        | {
            stream?: string;
            data?: {
              text?: string;
              delta?: string;
              state?: string;
              errorMessage?: string;
              tool?: string;
              toolName?: string;
            };
            runId?: string;
            sessionKey?: string;
          }
        | undefined;

      if (payload == null) return;

      const { data } = payload;
      if (data == null) return;

      if (data.state === 'error') {
        setState((prev) => ({
          ...prev,
          processing: null,
          messages: [
            ...closeStreamingMessages(prev.messages),
            {
              id: `error-${nextMsgId++}`,
              role: 'assistant' as const,
              content: `Error: ${data.errorMessage ?? 'Unknown error'}`,
              timestamp: Date.now(),
            },
          ],
        }));
        return;
      }

      // Streaming assistant text — use delta to update the last message
      if (payload.stream === 'assistant' && data.delta != null) {
        const { runId } = payload;
        setState((prev) => {
          const lastMsg = prev.messages[prev.messages.length - 1];
          if (
            lastMsg != null &&
            lastMsg.role === 'assistant' &&
            lastMsg.isStreaming === true &&
            lastMsg.runId === runId
          ) {
            const updated = {
              ...lastMsg,
              content: lastMsg.content + data.delta!,
            };
            return {
              ...prev,
              processing: null,
              messages: [...prev.messages.slice(0, -1), updated],
            };
          }
          return {
            ...prev,
            processing: null,
            messages: [
              ...closeStreamingMessages(prev.messages),
              {
                id: `assistant-${nextMsgId++}`,
                role: 'assistant' as const,
                content: data.delta!,
                timestamp: Date.now(),
                isStreaming: true,
                runId,
              },
            ],
          };
        });
        return;
      }

      if (typeof data.state === 'string' && data.state.length > 0) {
        const stateKey = data.state.toLowerCase();
        if (TERMINAL_PROCESSING_STATES.has(stateKey)) {
          setState((prev) => ({
            ...prev,
            processing: null,
            messages: closeStreamingMessages(prev.messages),
          }));
          return;
        }
        const label = resolveProcessingLabel(data.state);
        const rawDetail = data.toolName ?? data.tool ?? data.text;
        const detail =
          rawDetail != null && rawDetail.length > 0 ? rawDetail : undefined;
        setState((prev) => {
          const lastMsg = prev.messages[prev.messages.length - 1];
          if (lastMsg?.isStreaming === true) return prev;
          return {
            ...prev,
            processing: { label, detail },
          };
        });
        return;
      }

      return;
    }

    if (evt.event === 'chat') {
      const payload = evt.payload as
        | {
            state?: string;
            text?: string;
            errorMessage?: string;
          }
        | undefined;

      if (payload?.state === 'error') {
        setState((prev) => ({
          ...prev,
          processing: null,
          messages: [
            ...closeStreamingMessages(prev.messages),
            {
              id: `error-${nextMsgId++}`,
              role: 'assistant' as const,
              content: `Error: ${payload.errorMessage ?? 'Unknown error'}`,
              timestamp: Date.now(),
            },
          ],
        }));
      }
      return;
    }

    if (
      evt.event === 'agent.approval' ||
      evt.event === 'approval.request' ||
      evt.event === 'tool.approval'
    ) {
      const payload = evt.payload as
        | { id?: string; command?: string; description?: string }
        | undefined;
      if (payload?.command != null) {
        const approval: ApprovalRequest = {
          approvalId: payload.id ?? `approval-${nextMsgId++}`,
          command: payload.command,
          description: payload.description,
          state: 'pending',
        };
        setState((prev) => ({
          ...prev,
          processing: null,
          messages: [
            ...closeStreamingMessages(prev.messages),
            {
              id: `approval-${nextMsgId++}`,
              role: 'assistant' as const,
              content: payload.description ?? `Approve: ${payload.command}`,
              timestamp: Date.now(),
              approval,
            },
          ],
        }));
      }
      return;
    }

    if (evt.event === 'agent.done' || evt.event === 'chat.done') {
      setState((prev) => {
        const closed = closeStreamingMessages(prev.messages);
        const lastMsg = closed[closed.length - 1];
        if (
          lastMsg != null &&
          lastMsg.role === 'assistant' &&
          lastMsg.approval == null
        ) {
          const approval = detectApprovalRequest(lastMsg.content);
          if (approval != null) {
            return {
              ...prev,
              processing: null,
              messages: [...closed.slice(0, -1), { ...lastMsg, approval }],
            };
          }
        }
        return { ...prev, processing: null, messages: closed };
      });
      return;
    }

    return;
  }

  // GatewayResponse — surface error responses as messages
  {
    const res = frame;
    if (!res.ok && res.error != null) {
      setState((prev) => ({
        ...prev,
        processing: null,
        messages: [
          ...closeStreamingMessages(prev.messages),
          {
            id: `error-${nextMsgId++}`,
            role: 'assistant' as const,
            content: `Error: ${res.error?.message ?? 'Request failed'}`,
            timestamp: Date.now(),
          },
        ],
      }));
    }
  }
}

/** Shape of a single message in the `chat.history` RPC response. */
interface HistoryMessage {
  readonly role?: string;
  readonly content?: string | ReadonlyArray<{ type?: string; text?: string }>;
  readonly timestamp?: number;
}

/**
 * Maximum number of messages OpenClaw's `chat.history` will return in one
 * call. Asking for more is silently clamped to this value upstream, and the
 * RPC has no cursor pagination — this is the deepest tail we can pull.
 */
const CHAT_HISTORY_GATEWAY_LIMIT = 1000;

/**
 * Fetch recent chat history from the gateway after authentication so the
 * chat view isn't blank on reconnect. Silently skips if the gateway doesn't
 * support `chat.history`.
 */
function fetchChatHistory(
  ws: AgentWebSocket,
  setState: Dispatch<SetStateAction<AgentState>>,
): void {
  ws.request<{ messages?: readonly HistoryMessage[] }>('chat.history', {
    limit: CHAT_HISTORY_GATEWAY_LIMIT,
  })
    .then((res) => {
      const seeded = normalizeHistoryMessages(res.messages);
      if (seeded.length === 0) return;

      setState((prev) => {
        if (prev.messages.length === 0) {
          return { ...prev, messages: seeded };
        }
        const seen = new Set(prev.messages.map(messageDedupKey));
        const additions = seeded.filter((m) => !seen.has(messageDedupKey(m)));
        if (additions.length === 0) return prev;
        const merged = [...prev.messages, ...additions].sort(
          (a, b) => a.timestamp - b.timestamp,
        );
        return { ...prev, messages: merged };
      });
    })
    .catch(() => {
      // Gateway doesn't support chat.history — silently skip.
    });
}

function messageDedupKey(m: ChatMessage): string {
  return `${m.timestamp}|${m.role}|${m.content}`;
}

function normalizeHistoryMessages(
  raw: readonly HistoryMessage[] | undefined,
): ChatMessage[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map((m: HistoryMessage, i: number) => {
    let text = '';
    if (typeof m.content === 'string') {
      text = m.content;
    } else if (Array.isArray(m.content)) {
      text = (m.content as ReadonlyArray<{ type?: string; text?: string }>)
        .filter((c) => c.type === 'text' && c.text != null)
        .map((c) => c.text!)
        .join('');
    }
    return {
      id: `history-${m.timestamp ?? 'x'}-${i}`,
      role: m.role === 'user' ? 'user' : 'assistant',
      content: text,
      timestamp: m.timestamp ?? Date.now(),
    };
  });
}

/**
 * Detect approval-request patterns in agent text messages, e.g.
 *   "please approve the `some command` command"
 *   "approval needed — please approve the `...` command"
 */
function detectApprovalRequest(text: string): ApprovalRequest | null {
  const approvePattern =
    /(?:please\s+)?approv(?:e|al\s+(?:needed|required))[^`]*`([^`]+)`/iu;
  const match = approvePattern.exec(text);
  if (match != null) {
    const desc = text.replace(approvePattern, '').trim();
    return {
      approvalId: `text-approval-${nextMsgId++}`,
      command: match[1] ?? '',
      description: desc.length > 0 ? desc : undefined,
      state: 'pending',
    };
  }
  return null;
}
